import { useState } from 'react'
import PropTypes from 'prop-types'
import { Alert, FileInput, FileListItem, Hint, Modal, PrimaryButton, Text, TextButton } from '@artprov/design-system'
import { ACCEPT_DDJJ, EXTENSIONES_DDJJ, MAX_DDJJ_BYTES } from './constants/modal-carga-ddjj.js'
import { combinarEstilosDdjj } from './utils/combinar-estilos-ddjj.js'
import { combinarTextosDdjj } from './utils/combinar-textos-ddjj.js'
import { validarArchivoDdjj, varianteHint } from './utils/validar-archivo-ddjj.js'
import { useCargaDdjj } from './hooks/useCargaDdjj.js'
import { useDescargaTemplateDdjj } from './hooks/useDescargaTemplateDdjj.js'

// PBI 81147: modal de carga de la DDJJ. Se usa la forma compuesta del Modal porque el
// AcceptButton del simple no acepta disabled (criterios 9, 10 y 14).
// `textos` y `estilos` son opcionales: lo que no se pasa toma el valor de la carga del médico
// (MODAL_CARGA_DDJJ y ESTILOS_MODAL_DDJJ). Las props de estilo se esparcen antes que las de
// comportamiento (onClick, disabled, value, aria-*), así que `estilos` no puede pisarlas.
// Las variantes de los Hint no se configuran: las define la validación del archivo.
export default function ModalDdjj({ open, onClose, textos, estilos, contrato, onCargada, validacion }) {
  const T = combinarTextosDdjj(textos)
  const E = combinarEstilosDdjj(estilos)
  const [archivo, setArchivo] = useState(null)
  // D8: sin `contrato` los hooks usan el contrato activo (apertura desde el banner).
  // Validación configurable: sin `validacion` se aplican las reglas de la carga del médico.
  const maxBytes = validacion?.maxBytes ?? MAX_DDJJ_BYTES
  const extensiones = validacion?.extensiones ?? EXTENSIONES_DDJJ
  const accept = validacion?.extensiones?.join(',') ?? ACCEPT_DDJJ
  const { formatoOk, tamanioOk, valido } = validarArchivoDdjj(archivo, {
    maxBytes,
    extensiones,
  })

  // Con éxito no hay request que abortar ni error que limpiar: sólo se resetea y se cierra.
  const { enviar, abortar, enviando, error, limpiarError } = useCargaDdjj({
    contrato,
    onExito: () => {
      setArchivo(null)
      onClose()
    },
    onCargada,
    mensajeError: T.errorGenerico,
    mensajeSesionVencida: T.sesionVencida,
    mensajeExito: T.exito,
  })

  // P1 resuelta: el template lo genera reporting (PERFIL_MEDICO_DDJJ) vía clientes_service.
  const {
    descargar,
    descargando,
    error: errorDescarga,
    limpiarError: limpiarErrorDescarga,
  } = useDescargaTemplateDdjj({
    contrato,
    nombreArchivo: T.template.archivo,
    mensajeError: T.errorDescarga,
  })

  const cerrar = () => {
    abortar()
    setArchivo(null)
    limpiarError()
    limpiarErrorDescarga()
    onClose()
  }

  const cambiarArchivo = (nuevo) => {
    limpiarError()
    setArchivo(nuevo)
  }

  return (
    <Modal.Container open={open} setOpen={(abierto) => !abierto && cerrar()}>
      <Modal.Content {...E.contenido}>
        <Modal.Header>
          <Modal.Title>{T.titulo}</Modal.Title>
          <Modal.Close onClose={cerrar} />
        </Modal.Header>

        <Modal.Body classNameScrollContainer="m-0">
          <div className="flex flex-col gap-4">
            <Text {...E.bajada}>{T.bajada}</Text>
            <Alert
              {...E.alerta}
              description={<span className="whitespace-pre-line">{T.alerta}</span>}
              isInteractive={false}
            />
            {/* Fila del template con FileListItem del DS (mockup: ícono, nombre, badge PDF, link Descargar) */}
            <FileListItem
              {...E.filaTemplate}
              files={[
                {
                  fileName: T.template.nombre,
                  fileType: T.template.tipo.toLowerCase(),
                  content: descargando ? T.template.descargando : T.template.link,
                },
              ]}
              variant="link"
              onAction={descargar}
              isDisabled={descargando}
            />
            {errorDescarga ? <Alert {...E.alertaError} description={errorDescarga} isInteractive={false} /> : null}

            <div className="flex flex-col gap-1">
              <Text {...E.labelArchivo} as="label" htmlFor="archivo-ddjj">
                {T.labelArchivo}
              </Text>
              <FileInput
                id="archivo-ddjj"
                name="archivo"
                accept={accept}
                placeholder={T.placeholder}
                value={archivo}
                onChange={cambiarArchivo}
                isInvalid={archivo !== null && !valido}
                isDisabled={enviando}
                aria-describedby="hint-formato-ddjj hint-tamanio-ddjj"
              />
              <Hint id="hint-formato-ddjj" variant={varianteHint(formatoOk)}>
                {T.hintFormato}
              </Hint>
              <Hint id="hint-tamanio-ddjj" variant={varianteHint(tamanioOk)}>
                {T.hintTamanio}
              </Hint>
            </div>

            {error ? <Alert {...E.alertaError} title={error} isInteractive={false} /> : null}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <TextButton {...E.botonCancelar} onClick={cerrar}>
            {T.cancelar}
          </TextButton>
          <PrimaryButton {...E.botonCargar} disabled={!valido || enviando} onClick={() => enviar(archivo)}>
            {enviando ? T.cargando : T.cargar}
          </PrimaryButton>
        </Modal.Footer>
      </Modal.Content>
    </Modal.Container>
  )
}

ModalDdjj.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  // D8: contrato de la fila de Mis cuentas; sin prop, el contrato activo.
  contrato: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  // Se llama después de un 201 o 409 para que el origen refresque sus datos.
  onCargada: PropTypes.func,
  // Reglas de validación opcionales; sin prop, las de la carga del médico (5 MB, imágenes + PDF).
  validacion: PropTypes.shape({
    maxBytes: PropTypes.number,
    extensiones: PropTypes.arrayOf(PropTypes.string),
  }),
  textos: PropTypes.shape({
    titulo: PropTypes.string,
    bajada: PropTypes.string,
    alerta: PropTypes.string,
    template: PropTypes.shape({
      nombre: PropTypes.string,
      tipo: PropTypes.string,
      link: PropTypes.string,
      descargando: PropTypes.string,
      archivo: PropTypes.string,
    }),
    labelArchivo: PropTypes.string,
    placeholder: PropTypes.string,
    hintFormato: PropTypes.string,
    hintTamanio: PropTypes.string,
    cancelar: PropTypes.string,
    cargar: PropTypes.string,
    cargando: PropTypes.string,
    errorGenerico: PropTypes.string,
    errorDescarga: PropTypes.string,
    sesionVencida: PropTypes.string,
  }),
  // Props del componente del DS de cada pieza; claves y valores por defecto en ESTILOS_MODAL_DDJJ.
  estilos: PropTypes.shape({
    contenido: PropTypes.object,
    bajada: PropTypes.object,
    alerta: PropTypes.object,
    filaTemplate: PropTypes.object,
    labelArchivo: PropTypes.object,
    alertaError: PropTypes.object,
    botonCancelar: PropTypes.object,
    botonCargar: PropTypes.object,
  }),
}
